import { platformStorageAttachedObjectReferenceSchema } from '@crest/platform-contracts/storage';
import { z } from 'zod';
export const documentsWorkforceProtectedFormsCapability = 'documents.workforce-protected-forms.v1';
export const documentsWorkforceProtectedFormEligibilityCapability = 'workforce.documents-protected-form-eligibility.v1';
export const documentsWorkforceProtectedFormSubmissionCapability = 'workforce.documents-protected-form-submission.v1';
export const documentsWorkforceProtectedFormEligibilitySlot = 'documents.workforce-protected-form-eligibility';
export const documentsWorkforceProtectedFormSubmissionSlot = 'documents.workforce-protected-form-submissions';
export const documentsWorkforceProtectedFormEligibilityProviderId = 'workforce.server.documents-protected-form-eligibility';
export const documentsWorkforceProtectedFormSubmissionProviderId = 'workforce.server.documents-protected-form-submission';
const uuidSchema = z.string().uuid();
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const reasonSchema = z.string().trim().min(10).max(500);
const assetKeySchema = z.string().trim().min(1).max(100).regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/, 'Documents Workforce form asset keys use lower-case letters, numbers, and hyphens.');
export const documentsWorkforceHrFormPurposeSchema = z.enum([
    'onboarding', 'tax-withholding', 'employment-eligibility', 'payroll-banking', 'incident',
    'performance-review', 'corrective-action', 'offboarding', 'other-personnel',
]);
export const documentsWorkforceHrFormSensitivitySchema = z.enum([
    'confidential', 'tax', 'employment-eligibility', 'banking', 'medical', 'personnel-restricted',
]);
export const documentsWorkforceHrFormAudienceSchema = z.enum([
    'self-service', 'manager-assigned', 'self-or-manager',
]);
export const documentsWorkforceHrFormFieldTypeSchema = z.enum([
    'text', 'multiline', 'date', 'number', 'currency', 'boolean', 'choice', 'multi-choice', 'file',
]);
export const documentsWorkforceFormAssetStatusSchema = z.enum(['draft', 'published', 'archived']);
export const documentsWorkforceHrFormFieldDefinitionSchema = z.strictObject({
    key: z.string().trim().min(1).max(120).regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/),
    label: z.string().trim().min(1).max(200),
    type: documentsWorkforceHrFormFieldTypeSchema,
    required: z.boolean(),
    sensitive: z.boolean(),
    options: z.array(z.string().trim().min(1).max(160)).max(100).default([]),
}).superRefine((field, context) => {
    const choice = field.type === 'choice' || field.type === 'multi-choice';
    if (choice !== (field.options.length > 0))
        context.addIssue({
            code: 'custom', path: ['options'], message: 'Only choice fields require nonempty options.',
        });
    if (new Set(field.options.map((option) => option.toLocaleLowerCase('en-US'))).size !==
        field.options.length)
        context.addIssue({
            code: 'custom', path: ['options'], message: 'Workforce HR form options must be unique.',
        });
});
export const documentsWorkforceHrFormDefinitionContentSchema = z.strictObject({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2_000).nullable(),
    purpose: documentsWorkforceHrFormPurposeSchema,
    sensitivity: documentsWorkforceHrFormSensitivitySchema,
    audience: documentsWorkforceHrFormAudienceSchema,
    documentsAssetId: uuidSchema,
    documentsAssetVersionId: uuidSchema,
    documentsAssetSha256: sha256Schema,
    fields: z.array(documentsWorkforceHrFormFieldDefinitionSchema).max(100),
    completedDocumentRequired: z.literal(true),
    signatureRequired: z.literal(true),
    initialsRequired: z.literal(true),
    consentText: z.string().trim().min(1).max(4_000),
    maximumAttachments: z.number().int().min(0).max(10),
    allowedAttachmentContentTypes: z.array(z.enum([
        'application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif',
    ])).max(5),
    notificationPolicy: z.strictObject({
        notificationOnly: z.literal(true), emailAttachmentsAllowed: z.literal(false),
    }),
}).superRefine((content, context) => {
    const keys = content.fields.map(({ key }) => key);
    if (new Set(keys).size !== keys.length)
        context.addIssue({
            code: 'custom', path: ['fields'], message: 'Workforce HR form field keys must be unique.',
        });
    if (content.maximumAttachments === 0 && content.allowedAttachmentContentTypes.length > 0) {
        context.addIssue({ code: 'custom', path: ['allowedAttachmentContentTypes'],
            message: 'A form that allows no attachments cannot declare attachment content types.' });
    }
    if (content.maximumAttachments > 0 && content.allowedAttachmentContentTypes.length === 0) {
        context.addIssue({ code: 'custom', path: ['allowedAttachmentContentTypes'],
            message: 'A form that allows attachments must declare allowed content types.' });
    }
});
export const documentsWorkforceProtectedFormEligibilityRequestSchema = z.strictObject({
    consumerModuleId: z.literal('documents'),
    requestedCapability: z.literal(documentsWorkforceProtectedFormsCapability),
    resourceType: z.literal('documents.workforce-protected-form-submission'),
    resourceId: uuidSchema,
    definitionId: uuidSchema,
    expectedDefinitionVersionId: uuidSchema,
    targetMembershipId: uuidSchema,
    expectedDocumentsAssetId: uuidSchema,
    expectedDocumentsAssetVersionId: uuidSchema,
});
export const documentsWorkforceProtectedFormEligibilityResponseSchema = z.strictObject({
    state: z.literal('eligible'),
    capability: z.literal(documentsWorkforceProtectedFormEligibilityCapability),
    tenantId: uuidSchema,
    actorMembershipId: uuidSchema,
    targetMembershipId: uuidSchema,
    definitionId: uuidSchema,
    definitionVersionId: uuidSchema,
    definitionVersionNumber: z.number().int().positive(),
    definitionContentHash: sha256Schema,
    definition: documentsWorkforceHrFormDefinitionContentSchema,
    factHash: sha256Schema,
});
export const documentsWorkforceProtectedFormSubmissionRequestSchema = z.strictObject({
    consumerModuleId: z.literal('documents'),
    requestedCapability: z.literal(documentsWorkforceProtectedFormSubmissionCapability),
    sourceCapability: z.literal(documentsWorkforceProtectedFormsCapability),
    commandId: uuidSchema,
    definitionId: uuidSchema,
    definitionVersionId: uuidSchema,
    targetMembershipId: uuidSchema,
    documentsSubmissionId: uuidSchema,
    documentsAssetId: uuidSchema,
    documentsAssetVersionId: uuidSchema,
    documentsAssetSha256: sha256Schema,
    submissionProofHash: sha256Schema,
    submittedAt: z.string().datetime({ offset: true }),
});
export const documentsWorkforceProtectedFormSubmissionReceiptResponseSchema = z.strictObject({
    state: z.literal('recorded'),
    capability: z.literal(documentsWorkforceProtectedFormSubmissionCapability),
    tenantId: uuidSchema,
    receiptId: uuidSchema,
    definitionId: uuidSchema,
    definitionVersionId: uuidSchema,
    targetMembershipId: uuidSchema,
    documentsSubmissionId: uuidSchema,
    submissionProofHash: sha256Schema,
    recordedAt: z.string().datetime({ offset: true }),
    idempotent: z.boolean(),
});
export const documentsWorkforceFormAssetVersionSchema = z.strictObject({
    id: uuidSchema,
    tenantId: uuidSchema,
    assetId: uuidSchema,
    versionNumber: z.number().int().positive(),
    sourceVersionId: uuidSchema.nullable(),
    format: z.enum(['fillable-pdf', 'fillable-docx']),
    sensitivity: documentsWorkforceHrFormSensitivitySchema,
    sourceObjectReference: platformStorageAttachedObjectReferenceSchema,
    contentHash: sha256Schema,
    reason: reasonSchema,
    createdByMembershipId: uuidSchema,
    createdAt: z.string().datetime({ offset: true }),
});
export const documentsWorkforceFormAssetSchema = z.strictObject({
    id: uuidSchema,
    tenantId: uuidSchema,
    key: assetKeySchema,
    name: z.string().trim().min(1).max(200),
    status: documentsWorkforceFormAssetStatusSchema,
    publishedVersionId: uuidSchema.nullable(),
    latestVersionId: uuidSchema,
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: uuidSchema,
    updatedByMembershipId: uuidSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    publishedVersion: documentsWorkforceFormAssetVersionSchema.nullable(),
    latestVersion: documentsWorkforceFormAssetVersionSchema,
});
export const documentsWorkforceFormAssetListQuerySchema = z.strictObject({
    includeArchived: z.boolean().default(false),
    sensitivity: documentsWorkforceHrFormSensitivitySchema.nullable().default(null),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().nonnegative().default(0),
});
const assetVersionInputSchema = z.strictObject({
    format: z.enum(['fillable-pdf', 'fillable-docx']),
    sensitivity: documentsWorkforceHrFormSensitivitySchema,
    sourceObjectReference: platformStorageAttachedObjectReferenceSchema,
}).superRefine((value, context) => {
    const expected = value.format === 'fillable-pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    if (value.sourceObjectReference.actualContentType !== expected)
        context.addIssue({
            code: 'custom', path: ['sourceObjectReference', 'actualContentType'],
            message: `The protected form asset must use ${expected}.`,
        });
});
export const documentsCreateWorkforceFormAssetSchema = z.strictObject({
    kind: z.literal('create'), commandId: uuidSchema, assetId: uuidSchema,
    key: assetKeySchema, name: z.string().trim().min(1).max(200),
    version: assetVersionInputSchema, reason: reasonSchema,
});
export const documentsAppendWorkforceFormAssetVersionSchema = z.strictObject({
    kind: z.literal('append-version'), commandId: uuidSchema,
    expectedRevision: z.number().int().positive(), version: assetVersionInputSchema,
    reason: reasonSchema,
});
export const documentsPublishWorkforceFormAssetSchema = z.strictObject({
    kind: z.literal('publish'), commandId: uuidSchema,
    expectedRevision: z.number().int().positive(), versionId: uuidSchema, reason: reasonSchema,
});
export const documentsWorkforceFormAssetStateCommandSchema = z.strictObject({
    kind: z.enum(['archive', 'restore']), commandId: uuidSchema,
    expectedRevision: z.number().int().positive(), reason: reasonSchema,
});
export const documentsRollbackWorkforceFormAssetSchema = z.strictObject({
    kind: z.literal('rollback'), commandId: uuidSchema,
    expectedRevision: z.number().int().positive(), sourceVersionId: uuidSchema, reason: reasonSchema,
});
export const documentsExistingWorkforceFormAssetCommandSchema = z.discriminatedUnion('kind', [
    documentsAppendWorkforceFormAssetVersionSchema,
    documentsPublishWorkforceFormAssetSchema,
    documentsWorkforceFormAssetStateCommandSchema,
    documentsRollbackWorkforceFormAssetSchema,
]);
export const documentsWorkforceFormAssetResponseSchema = z.strictObject({
    data: documentsWorkforceFormAssetSchema,
    command: z.strictObject({ commandId: uuidSchema, idempotent: z.boolean() }).optional(),
});
export const documentsWorkforceFormAssetsResponseSchema = z.strictObject({
    data: z.array(documentsWorkforceFormAssetSchema),
    page: z.strictObject({ limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative() }),
});
export const documentsWorkforceFormAssetVersionsResponseSchema = z.strictObject({
    data: z.array(documentsWorkforceFormAssetVersionSchema),
});
export const documentsWorkforceProtectedFormFieldPresenceSchema = z.strictObject({
    fieldKey: z.string().trim().min(1).max(120).regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/),
    present: z.boolean(),
});
export const documentsWorkforceProtectedFormSignatureSchema = z.strictObject({
    initials: z.string().trim().min(1).max(20),
    typedName: z.string().trim().min(1).max(240),
    signatureSha256: sha256Schema,
    signatureObjectReference: platformStorageAttachedObjectReferenceSchema.nullable().default(null),
});
export const documentsCreateWorkforceProtectedFormSubmissionSchema = z.strictObject({
    commandId: uuidSchema,
    submissionId: uuidSchema,
    definitionId: uuidSchema,
    expectedDefinitionVersionId: uuidSchema,
    targetMembershipId: uuidSchema,
    documentAssetId: uuidSchema,
    expectedDocumentAssetVersionId: uuidSchema,
    completedFormReference: platformStorageAttachedObjectReferenceSchema,
    fieldPresence: z.array(documentsWorkforceProtectedFormFieldPresenceSchema).max(100),
    signature: documentsWorkforceProtectedFormSignatureSchema,
    supportingAttachments: z.array(platformStorageAttachedObjectReferenceSchema).max(10).default([]),
    consentAccepted: z.literal(true),
    reason: reasonSchema,
}).superRefine((value, context) => {
    if (value.completedFormReference.actualContentType !== 'application/pdf')
        context.addIssue({
            code: 'custom', path: ['completedFormReference', 'actualContentType'],
            message: 'A protected employee form submission must retain a verified PDF.',
        });
    const keys = value.fieldPresence.map(({ fieldKey }) => fieldKey);
    if (new Set(keys).size !== keys.length)
        context.addIssue({
            code: 'custom', path: ['fieldPresence'], message: 'Field presence keys must be unique.',
        });
    const objectIds = [value.completedFormReference, ...value.supportingAttachments,
        ...(value.signature.signatureObjectReference === null
            ? [] : [value.signature.signatureObjectReference])].map(({ objectId }) => objectId);
    if (new Set(objectIds).size !== objectIds.length)
        context.addIssue({
            code: 'custom', message: 'Each protected form object reference may be used once.',
        });
});
export const documentsWorkforceProtectedFormSubmissionSchema = z.strictObject({
    contractVersion: z.literal(documentsWorkforceProtectedFormsCapability),
    tenantId: uuidSchema,
    id: uuidSchema,
    commandId: uuidSchema,
    targetMembershipId: uuidSchema,
    submittedByMembershipId: uuidSchema,
    definitionId: uuidSchema,
    definitionVersionId: uuidSchema,
    definitionVersionNumber: z.number().int().positive(),
    definitionContentHash: sha256Schema,
    purpose: documentsWorkforceHrFormPurposeSchema,
    sensitivity: documentsWorkforceHrFormSensitivitySchema,
    documentAssetId: uuidSchema,
    documentAssetVersionId: uuidSchema,
    documentAssetSha256: sha256Schema,
    completedFormReference: platformStorageAttachedObjectReferenceSchema,
    completedFormSha256: sha256Schema,
    fieldPresenceHash: sha256Schema,
    signature: documentsWorkforceProtectedFormSignatureSchema,
    supportingAttachments: z.array(platformStorageAttachedObjectReferenceSchema).max(10),
    submissionProofHash: sha256Schema,
    requestHash: sha256Schema,
    workforceReceiptId: uuidSchema,
    submittedAt: z.string().datetime({ offset: true }),
    reason: reasonSchema,
    createdAt: z.string().datetime({ offset: true }),
});
export const documentsWorkforceProtectedFormSubmissionListQuerySchema = z.strictObject({
    targetMembershipId: uuidSchema.nullable().default(null),
    definitionId: uuidSchema.nullable().default(null),
    sensitivity: documentsWorkforceHrFormSensitivitySchema.nullable().default(null),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const documentsWorkforceProtectedFormSubmissionResponseSchema = z.strictObject({
    data: documentsWorkforceProtectedFormSubmissionSchema,
    command: z.strictObject({ commandId: uuidSchema, idempotent: z.boolean() }).optional(),
});
export const documentsWorkforceProtectedFormSubmissionsResponseSchema = z.strictObject({
    data: z.array(documentsWorkforceProtectedFormSubmissionSchema),
    page: z.strictObject({ limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative() }),
});
//# sourceMappingURL=documents-workforce-protected-form.contract.js.map